import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export default function BeforeAfterSlider({ beforeImage, afterImage, title, className = "", position = "center" }) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const position = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(position);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    if (e.touches.length > 0) {
      handleMove(e.touches[0].clientX);
    }
  };

  useEffect(() => {
    const handleMouseUp = () => setIsDragging(false);
    if (isDragging) {
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchend', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Slider Container */}
      <div 
        ref={containerRef}
        className="relative overflow-hidden rounded-2xl aspect-[4/3] shadow-md group cursor-ew-resize select-none bg-stone-200 touch-none"
        onMouseDown={(e) => {
          e.preventDefault();
          setIsDragging(true);
          handleMove(e.clientX);
        }}
        onTouchStart={(e) => {
          setIsDragging(true);
          if (e.touches.length > 0) {
            handleMove(e.touches[0].clientX);
          }
        }}
        onMouseMove={handleMouseMove}
        onTouchMove={handleTouchMove}
      >
        {/* After Image (Background) */}
        <img 
          src={afterImage} 
          alt="Efter" 
          className={`absolute inset-0 w-full h-full object-cover pointer-events-none ${
            position === 'top' ? 'object-top' : 'object-center'
          }`}
        />

        {/* Before Image (Foreground with Clip Path) */}
        <img 
          src={beforeImage} 
          alt="Før" 
          className={`absolute inset-0 w-full h-full object-cover pointer-events-none ${
            position === 'top' ? 'object-top' : 'object-center'
          }`}
          style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
        />

        {/* Tag: Før (Before) */}
        <span className="absolute top-4 left-4 bg-black/40 backdrop-blur-xs text-white text-xs font-bold px-3 py-1.5 rounded-md tracking-wider uppercase pointer-events-none">
          Før
        </span>

        {/* Tag: Efter (After) */}
        <span className="absolute top-4 right-4 bg-black/40 backdrop-blur-xs text-white text-xs font-bold px-3 py-1.5 rounded-md tracking-wider uppercase pointer-events-none">
          Efter
        </span>

        {/* Split Line */}
        <div 
          className="absolute inset-y-0 w-1 bg-white shadow-lg pointer-events-none"
          style={{ left: `${sliderPosition}%` }}
        />

        {/* Center Split Handle */}
        <div 
          className="absolute inset-y-0 -translate-x-1/2 flex items-center justify-center pointer-events-none"
          style={{ left: `${sliderPosition}%` }}
        >
          <div className="w-10 h-10 rounded-full bg-transparent border-2 border-white flex items-center justify-center shadow-xl transition-transform group-hover:scale-110">
            <div className="flex items-center justify-between w-full px-1 text-white">
              <ChevronLeft size={16} strokeWidth={3} />
              <ChevronRight size={16} strokeWidth={3} />
            </div>
          </div>
        </div>
      </div>

      {/* Title */}
      {title && (
        <h4 className="mt-4 text-center text-teal-950 font-bold text-lg">
          {title}
        </h4>
      )}
    </div>
  );
}
